import {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import { useLocation, useRouteMatch } from "react-router";
import { Typography } from "@mui/material";
import { debounce, filter, join, orderBy } from "lodash";
import { ArrayParam, useQueryParams } from "use-query-params";

import { DESIGNS } from "helpers/apis";
import useAxios from "helpers/axios";
import useApp from "hooks/useApp";
import useAuth from "hooks/useAuth";
import {
  Workflow,
  IDesign,
  IDisease,
  DesignType,
  PlatformType,
} from "types/design";
import {
  CatalogFilterType,
  ICatalogSearchTerm,
  ITypeFilter,
  ITypeFilterOption,
} from "types/catalog";
import { GeneType, ISort } from "types/common";

interface ICatalogContextType {
  currentPage: number;
  customPanels: Partial<IDesign>[];
  filters: ITypeFilter[];
  genes: ICatalogSearchTerm[];
  isPartnerIncluded: boolean;
  missingTargets: string[];
  loading: boolean;
  loaded: boolean;
  rowsData: Partial<IDesign>[];
  suggestedPanels: Partial<IDesign>[];
  sortBy: ISort<IDesign> | null;
  totalCount: number;
  totalQueryGenes: number;
  getCatalogById: Function;
  getCustomPanelById: Function;
  resetFilters: () => void;
  setCurrentPage: (newPage: number) => void;
  setCustomPanels: Function;
  setFilters: (filters: ITypeFilter[]) => void;
  setGenes: Function;
  setMissingTargets: Function;
  setRowsData: Function;
  setSuggestedPanels: Function;
  setSortBy: (sort: ISort<IDesign>) => void;
}

export const DEFAULT_FILTERS: ITypeFilter[] = [
  {
    type: CatalogFilterType.ASSAY,
    label: "Workflow",
    expanded: true,
    options: [
      {
        id: 1,
        label: (
          <Typography variant="h6">
            <Typography component="span" variant="subtitle2" color="secondary">
              FUSION
            </Typography>
            plex (RNA)
          </Typography>
        ),
        type: Workflow.FUSION,
      },
      {
        id: 2,
        label: (
          <Typography variant="h6">
            <Typography component="span" variant="subtitle2" color="primary">
              VARIANT
            </Typography>
            plex (DNA)
          </Typography>
        ),
        type: Workflow.VARITANT,
      },
      {
        id: 3,
        label: (
          <Typography variant="h6">
            <Typography component="span" variant="subtitle2" color="error">
              LIQUID
            </Typography>
            plex (cfDNA)
          </Typography>
        ),
        type: Workflow.LIQUID,
      },
      // {
      //   id: 4,
      //   label: "Immunoverse (RNA)",
      //   type: Workflow.IMMUNOVERSE,
      // },
    ],
  },
  {
    type: CatalogFilterType.DISEASE,
    label: "Disease Type",
    expanded: true,
    options: [],
  },
  {
    type: CatalogFilterType.PLATFORM,
    label: "Platform Type",
    expanded: true,
    options: [
      {
        id: 1,
        label: "Ion Torrent",
        type: PlatformType.IONTORRENT,
      },
      {
        id: 2,
        label: "Illumina",
        type: PlatformType.ILLUMINA,
      },
    ],
  },
  {
    type: CatalogFilterType.DESIGN,
    label: "Design Type",
    expanded: true,
    options: [
      {
        id: 1,
        label: "Catalog Panel",
        type: DesignType.CATALOG_PANEL,
      },
      {
        id: 2,
        label: "Gene Module",
        type: DesignType.GENE_MODULE,
      },
    ],
  },
];

export const CatalogContext = createContext<ICatalogContextType>(
  {} as ICatalogContextType
);

export function CatalogProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalQueryGenes, setTotalQueryGenes] = useState<number>(0);
  const { user } = useAuth();
  const { diseaseTypes } = useApp();
  const [missingTargets, setMissingTargets] = useState<string[]>([]);
  const [isPartnerIncluded, setIsParterIncluded] = useState<boolean>(false);
  const { current: debouncedFetch } = useRef(
    debounce((filters, genes, currentPage, params, currentTotal) => {
      const fetchParams: any = {
        ...params,
      };
      if (genes.length) {
        const listOfGenes = genes
          .filter((gene: ICatalogSearchTerm) => gene.type === GeneType.Gene)
          .map((gene: ICatalogSearchTerm) => gene.name);
        const listOfAlias = genes
          .filter((gene: ICatalogSearchTerm) => gene.type === GeneType.Alias)
          .map((gene: ICatalogSearchTerm) => gene.name);
        const listOfGeneAlias = genes
          .filter(
            (gene: ICatalogSearchTerm) => gene.type === GeneType.GeneAlias
          )
          .map((gene: ICatalogSearchTerm) => gene.name);
        const listOfFusion = genes
          .filter((gene: ICatalogSearchTerm) => gene.type === GeneType.Fusion)
          .map((gene: ICatalogSearchTerm) => gene.name);
        if (genes.length > 1 || listOfFusion.length) {
          fetchParams.targets = genes
            .map((gene: ICatalogSearchTerm) => gene.name)
            .join("/");
        } else if (listOfGenes.length) {
          fetchParams.targets = listOfGenes.join();
        } else if (listOfAlias.length) {
          fetchParams.targets = listOfAlias.join();
        } else if (listOfGeneAlias.length) {
          fetchParams.targets = listOfGeneAlias.join();
        }
      }
      if (!params || Object.keys(params).length === 0) {
        filters.forEach((filterItem: ITypeFilter) => {
          const selectedOptions: ITypeFilterOption[] = filter(
            filterItem.options,
            {
              selected: true,
            }
          );
          if (selectedOptions.length) {
            switch (filterItem.type) {
              case CatalogFilterType.ASSAY:
                const selectedWorkflow = selectedOptions.map(
                  (filterOption: ITypeFilterOption) => filterOption.id
                );
                fetchParams.workflow = join(selectedWorkflow);
                break;
              case CatalogFilterType.DISEASE:
                const selectedDiseaseTypes = selectedOptions.map(
                  (filterOption: ITypeFilterOption) => filterOption.id
                );
                fetchParams.diseaseType = join(selectedDiseaseTypes);
                break;
              case CatalogFilterType.PLATFORM:
                const selectedPlatformTypes = selectedOptions.map(
                  (filterOption: ITypeFilterOption) => filterOption.type
                );
                fetchParams.platformType = join(selectedPlatformTypes);
                break;
              case CatalogFilterType.DESIGN:
                const selectedDesignTypes = selectedOptions.map(
                  (filterOption: ITypeFilterOption) => filterOption.type
                );
                fetchParams.designType = join(selectedDesignTypes);
                break;
            }
          }
        });
        // Add a pagination only on catalog page, but not evaluate panel options page
        fetchParams.pageNumber = currentPage;
        fetchParams.pageSize = 20;
      }
      fetchData(
        {
          params: fetchParams,
        },
        ({
          response: {
            data,
            totalCount: newTotalCount,
            numberQueryGenes,
            suggestedPanels,
            missingTargets,
            isPartnerIncluded: newPartnerIncluded,
          },
        }: {
          response: {
            data: Array<IDesign>;
            totalCount: number;
            numberQueryGenes?: number;
            suggestedPanels?: any[];
            missingTargets?: string[];
            isPartnerIncluded: number;
          };
        }) => {
          if (Array.isArray(data)) {
            setRowsData(data);
            if (currentTotal !== newTotalCount) {
              setTotalCount(newTotalCount);
              setCurrentPage(1);
            }
          } else {
            setRowsData([]);
            setTotalCount(0);
            setCurrentPage(1);
          }
          if (typeof numberQueryGenes === "number") {
            setTotalQueryGenes(numberQueryGenes);
          } else {
            setTotalQueryGenes(0);
          }
          setSuggestedPanels(
            Array.isArray(suggestedPanels) ? suggestedPanels : []
          );
          setMissingTargets(
            Array.isArray(missingTargets) ? missingTargets : []
          );
          setIsParterIncluded(Boolean(newPartnerIncluded));
        }
      );
    }, 500)
  );
  const { fetchData, loading, loaded } = useAxios({
    config: { url: DESIGNS },
    callOnInit: false,
  });
  const [filters, setFilters] = useState<ITypeFilter[]>(DEFAULT_FILTERS);
  const [sortBy, setSortBy] = useState<ISort<IDesign> | null>(null);
  const [rowsData, setRowsData] = useState<Partial<IDesign>[]>([]);
  const [customPanels, setCustomPanels] = useState<Partial<IDesign>[]>([]); // It's used only on evaluate panel options page
  const [suggestedPanels, setSuggestedPanels] = useState<Partial<IDesign>[]>(
    []
  ); // It's used only on evaluate panel options page
  const [genes, setGenes] = useState<ICatalogSearchTerm[]>([]);
  const [params, setParams] = useState<any>({});
  const { search } = useLocation();
  const { path, isExact } = useRouteMatch();
  // Query Params
  const [query, setQuery] = useQueryParams({
    workflows: ArrayParam,
    diseaseTypes: ArrayParam,
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const newSearchTerm: ICatalogSearchTerm[] = [];
    // Update catalogs only on catalog page
    if (path === "/catalog" && isExact) {
      for (let type of queryParams.keys()) {
        if (Object.values(GeneType).includes(type as any)) {
          queryParams
            .get(type)
            ?.split(",")
            .forEach((value: string) =>
              newSearchTerm.push({
                type: type as GeneType,
                name: value,
              })
            );
        }
      }
      setGenes(newSearchTerm);
    } else {
      if (queryParams.get("foundGenes")) {
        setParams((params: any) => {
          params.targets = queryParams.get("foundGenes");
          params.designType = DesignType.CATALOG_PANEL;
          params.suggestPanels = true;
          return params;
        });
      }
      if (queryParams.get("workflow")) {
        setParams((params: any) => {
          params.workflow = queryParams.get("workflow");
          return params;
        });
      }
      if (queryParams.get("diseaseType")) {
        setParams((params: any) => {
          params.diseaseType = queryParams.get("diseaseType");
          return params;
        });
      }
    }
  }, [search, path, isExact]);

  useEffect(() => {
    setFilters((filters) =>
      filters.map((filter: ITypeFilter) => {
        if (filter.type === CatalogFilterType.DISEASE) {
          return {
            ...filter,
            options: diseaseTypes.map((diseaseType: IDisease) => {
              return {
                id: diseaseType.id,
                label: diseaseType.name,
                selected:
                  Array.isArray(query.diseaseTypes) &&
                  query.diseaseTypes.includes(diseaseType.name),
              };
            }),
          };
        }
        if (
          filter.type === CatalogFilterType.ASSAY &&
          query.workflows?.length
        ) {
          return {
            ...filter,
            options: filter.options.map((filterOption: ITypeFilterOption) => {
              if (query.workflows!.includes(filterOption.type as Workflow)) {
                return { ...filterOption, selected: true };
              }
              return filterOption;
            }),
          };
        }
        return filter;
      })
    );
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [diseaseTypes]);

  // Change query params based on filter change
  useEffect(() => {
    // Workflows
    const selectedWorkflows = filters
      .find((filter) => filter.type === CatalogFilterType.ASSAY)!
      .options.filter((filterOption) => filterOption.selected);
    // Disease Types
    const selectedDiseaseTypes = filters
      .find((filter) => filter.type === CatalogFilterType.DISEASE)!
      .options.filter((filterOption) => filterOption.selected);
    setQuery({
      workflows: selectedWorkflows.length
        ? (selectedWorkflows.map((workflow) => workflow.type) as string[])
        : undefined,
      diseaseTypes: selectedDiseaseTypes.length
        ? (selectedDiseaseTypes.map(
            (diseaseType) => diseaseType.label
          ) as string[])
        : undefined,
    });
  }, [filters]);

  const memoedValue = useMemo(
    () => ({
      currentPage,
      customPanels,
      filters,
      genes,
      isPartnerIncluded,
      loading,
      loaded,
      missingTargets,
      rowsData: [...customPanels, ...rowsData],
      suggestedPanels,
      sortBy,
      totalCount: totalCount + customPanels.length,
      totalQueryGenes,
      getCatalogById: (id: number) => {
        return rowsData.find((item: Partial<IDesign>) => item.id === id);
      },
      getCustomPanelById: (id: number) => {
        return customPanels.find((item: Partial<IDesign>) => item.id === id);
      },
      resetFilters: () =>
        setFilters(
          filters.map((filter: ITypeFilter) => {
            return {
              ...filter,
              options: filter.options.map((filterOption: ITypeFilterOption) => {
                return {
                  ...filterOption,
                  selected: false,
                };
              }),
            };
          })
        ),
      setCurrentPage,
      setCustomPanels,
      setFilters,
      setGenes,
      setMissingTargets,
      setRowsData,
      setSuggestedPanels,
      setSortBy,
    }),
    /* eslint-disable react-hooks/exhaustive-deps */
    [
      currentPage,
      filters,
      genes,
      loading,
      missingTargets,
      rowsData,
      customPanels,
      suggestedPanels,
      sortBy,
      isPartnerIncluded,
    ]
  );

  // Change disabled status of Gene Module by user login status
  useEffect(() => {
    setFilters((filters) =>
      filters.map((filter: ITypeFilter) => {
        if (filter.type !== CatalogFilterType.DESIGN) return filter;
        return {
          ...filter,
          options: filter.options.map((filterOption: ITypeFilterOption) => {
            if (filterOption.type === DesignType.GENE_MODULE) {
              const isAuthenticated = !!user;
              return {
                ...filterOption,
                disabled: !isAuthenticated,
                selected: !isAuthenticated ? false : filterOption.selected,
              };
            }
            return filterOption;
          }),
        };
      })
    );
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [user]);

  // For fusion search, update a workflow type automatically
  useEffect(() => {
    if (genes.length > 1) {
      setFilters((filters) =>
        filters.map((filter: ITypeFilter) => {
          if (filter.type === CatalogFilterType.ASSAY) {
            return {
              ...filter,
              options: filter.options.map((filterOption: ITypeFilterOption) => {
                if (filterOption.type === Workflow.FUSION) {
                  return { ...filterOption, selected: true };
                }
                return { ...filterOption, selected: false };
              }),
            };
          }
          return filter;
        })
      );
    }
  }, [genes]);

  useEffect(() => {
    let newRowsData = [...rowsData];
    if (sortBy) {
      newRowsData = orderBy(newRowsData, [sortBy.field], [sortBy.order]);
    }
    setRowsData(newRowsData);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [sortBy]);
  // Reload catalog panels on filter change
  useEffect(() => {
    if (debouncedFetch) {
      debouncedFetch(filters, genes, currentPage, params, totalCount);
    }
  }, [
    JSON.stringify(filters.map((filter) => filter.options)),
    genes,
    currentPage,
    JSON.stringify(params),
  ]);

  return (
    <CatalogContext.Provider value={memoedValue}>
      {children}
    </CatalogContext.Provider>
  );
}

export default function useCatalog() {
  return useContext(CatalogContext);
}
